import { useEffect, useState } from 'preact/hooks';
import type { EnumSetterWidget, EnumRenderHint, EnumOption } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import './EnumSetterRenderer.css';

interface Props {
  widget: EnumSetterWidget;
  source: IPointBindingSource;
}

/**
 * One enum, three shapes (concept §3). RenderHint = auto picks based on
 * option count so the same JSON drives Segmented ≤ 4, Tile ≤ 8, Dropdown
 * for the rest — operators don't have to choose.
 *
 * Feedback point is optional: when present, the "active" tile is the one
 * the controller actually reports; when absent, we optimistically show the
 * last-sent-value so the widget isn't visually inert. That's a conscious
 * degradation — the concept prefers Ack+Readback (§1) whenever feedback is
 * available.
 */
export function EnumSetterRenderer({ widget, source }: Props) {
  const feedback = widget.feedbackPoint ? pointSignal(source, widget.feedbackPoint) : null;
  useEffect(() => feedback?.release ?? (() => {}), [feedback]);

  const [pending, setPending] = useState<{ target: unknown; state: 'sending' | 'rejected' } | null>(null);
  const [optimistic, setOptimistic] = useState<unknown>(null);

  const current = feedback?.value.value;
  const stale = feedback != null && isDegraded(current);
  const feedbackValue = current?.value ?? null;
  const activeValue = feedback ? feedbackValue : optimistic;

  const canWrite = !pending || pending.state === 'rejected';

  async function commit(target: unknown) {
    if (!canWrite) return;
    setPending({ target, state: 'sending' });
    setOptimistic(target);
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.commandPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPending(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPending({ target, state: 'rejected' });
          return;
        }
      }
      setPending(null);
    } catch {
      setPending(null);
    }
  }

  const shape = pickShape(widget.renderHint, widget.options.length);

  return (
    <div class={`hv-enum${stale ? ' hv-enum--stale' : ''}${pending ? ` hv-enum--${pending.state}` : ''}`}>
      {widget.label && <div class="hv-enum__label">{widget.label}</div>}
      {shape === 'segmented' && (
        <Segmented options={widget.options} active={activeValue} onPick={commit} disabled={!canWrite} />
      )}
      {shape === 'tile' && (
        <Tiles options={widget.options} active={activeValue} onPick={commit} disabled={!canWrite} />
      )}
      {shape === 'dropdown' && (
        <Dropdown options={widget.options} active={activeValue} onPick={commit} disabled={!canWrite} />
      )}
      {pending && (
        <div class="hv-enum__pending" role="status">
          {pending.state === 'sending' && <span>… gesendet, warte auf Regler</span>}
          {pending.state === 'rejected' && <span>Regler hat den Wert verworfen</span>}
        </div>
      )}
    </div>
  );
}

function pickShape(hint: EnumRenderHint | undefined, count: number): 'segmented' | 'tile' | 'dropdown' {
  if (hint && hint !== 'auto') return hint;
  if (count <= 4) return 'segmented';
  if (count <= 8) return 'tile';
  return 'dropdown';
}

function Segmented({ options, active, onPick, disabled }: {
  options: EnumOption[]; active: unknown; onPick(v: unknown): void; disabled: boolean;
}) {
  return (
    <div class="hv-enum__segmented" role="radiogroup">
      {options.map((o) => {
        const isActive = valuesEqual(o.value, active);
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            class={`hv-enum__seg${isActive ? ' hv-enum__seg--active' : ''}`}
            style={isActive && o.colour ? { background: o.colour } : undefined}
            onClick={() => onPick(o.value)}
            disabled={disabled}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Tiles({ options, active, onPick, disabled }: {
  options: EnumOption[]; active: unknown; onPick(v: unknown): void; disabled: boolean;
}) {
  return (
    <div class="hv-enum__tiles" role="radiogroup">
      {options.map((o) => {
        const isActive = valuesEqual(o.value, active);
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            class={`hv-enum__tile${isActive ? ' hv-enum__tile--active' : ''}`}
            style={isActive && o.colour ? { background: o.colour } : undefined}
            onClick={() => onPick(o.value)}
            disabled={disabled}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Dropdown({ options, active, onPick, disabled }: {
  options: EnumOption[]; active: unknown; onPick(v: unknown): void; disabled: boolean;
}) {
  return (
    <select
      class="hv-enum__dropdown"
      value={String(active ?? '')}
      onChange={(e) => {
        const raw = (e.currentTarget as HTMLSelectElement).value;
        const hit = options.find((o) => String(o.value) === raw);
        if (hit) onPick(hit.value);
      }}
      disabled={disabled}
    >
      {active == null && <option value="">— wählen —</option>}
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
      ))}
    </select>
  );
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}
